import { useEffect } from 'react';
import { IVisualPropertySelector, IVisualPropertyValue } from "powerbi-models";
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { runInAction, toJS } from "mobx";
import { useLocalObservable } from 'mobx-react'
import { IBasicFilter, FilterType, FiltersOperations } from "powerbi-models";

import { ProjectFilters, RangeFilter } from '../../apiclients/bstinsights';
import { default as useBSTInsightsAPI } from '../../apiclients/api/bstinsightsapi';
import { getVisualByGuid } from '../../components/EmbeddedReport/utils/getVisualByGuid';
import { default as useAcquireToken } from '../../components/AuthToken/Hooks/useAcquireToken';
import { ComponentsEnum } from '../../utils/components';

import {
  FILTER_VALUES_LIMIT,
  PROJECTS_LIMIT_SIZE,
  PROJECT_SUMMARY_TABLE,
  FILTER_APPLY_DELAY_MS,
} from '../utils/constants';

import {
  ViewType,
  ProjectStore,
  FilterItemType,
  UserPreferences,
  RangeFilterType,
  StringFilterType,
} from './types';

export const useProjectStore = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const azureIdentityId = activeAccount?.localAccountId || "";
  const { getTokenSilently, hasSupportGroup } = useAcquireToken();

  const {
    getFilters,
    getProjects,
    getProjectsIds,
    getUserPreferences,
    upsertUserPreferences,
  } = useBSTInsightsAPI();

  let debounceTimer: NodeJS.Timeout;

  useEffect(() => {
    return () => clearTimeout(debounceTimer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Fetch component preference
   * @param componentName ComponentsEnum
   * @returns Promise<string | undefined>
   */
  /* istanbul ignore next */
  const fetchPreference = async (componentName: ComponentsEnum) => {
    const accessToken = await getTokenSilently();
    const result = await getUserPreferences(
      accessToken,
      azureIdentityId,
      componentName
    );
    try {
      if (result) {
        const settings: UserPreferences = JSON.parse(result.settings || "{}");
        return settings?.value;
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Save component preference
   * @param componentName ComponentsEnum
   * @param payload UserPreferences
   */
  /* istanbul ignore next */
  const savePreference = async (componentName: ComponentsEnum, payload: UserPreferences) => {
    const accessToken = await getTokenSilently();
    try {
      const request = {
        azureIdentityId,
        componentName,
        settings: JSON.stringify(payload)
      }
      upsertUserPreferences(accessToken, request);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Return Sort UI fields for Visual
   * @param selectedSort string
   * @returns { value: IVisualPropertyValue, property: IVisualPropertySelector }
   */
  /* istanbul ignore next */
  const handleSortProperty = (selectedSort: string): {
    value: IVisualPropertyValue,
    property: IVisualPropertySelector
  } => {
    let abbrev = "";
    let selectedSortProperty = "";

    if (
      selectedSort === "Project Director Name" ||
      selectedSort === "Project Manager Name"
    ) {
      abbrev = selectedSort === "Project Director Name" ? ",(PD)" : ",(PM)";
      selectedSortProperty = selectedSort;
    }

    return {
      value: {
        value: `${selectedSortProperty}${abbrev}`,
        schema: "http://powerbi.com/product/schema#property"
      },
      property: {
        objectName: "visualSettings",
        propertyName: "sortingField",
      },
    };
  };

  return useLocalObservable<ProjectStore>(() => ({
    view: ViewType.MyProjects,
    sortBy: "",
    filters: undefined,
    searchText: "",
    hasOverrides: undefined,
    hasPredictions: true,

    projectIds: [],
    filterValues: {
      projectClientName: [],
      projectCompanyName: [],
      projectManagerName: [],
      projectDirectorName: [],
      projectOrganizationName: [],
    },

    filterItems: [],
    noProjectMessage: "",
    visualLimitedView: false,
    powerBIFilterLimitError: false,

    /**
     * Set sortBy
     * @param value string
     */
    setSortBy(value) {
      this.sortBy = value;
    },

    /**
     * Reset filters
     */
    resetFilters() {
      this.filters = undefined;
    },

    /**
     * Reset filters
     * Set search text
     * @param value string
     */
    setSearchText(value) {
      this.resetFilters();
      this.searchText = value;
    },

    /**
     * Reset filters
     * Reset search text
     * Set view
     * Set hasOverrides
     * Set hasPredictions
     * @param value ViewType
     */
    /* istanbul ignore next */
    setSelectedView(value) {
      this.resetFilters();
      this.setSearchText("");
      this.view = value;
      this.hasOverrides = value === ViewType.ProjectsWithOverrides || undefined;
      this.hasPredictions =
        value === ViewType.MyProjects ||
        value === ViewType.ProjectsWithPredictions ||
        undefined;
    },

    /**
     * Parse this.filters and return only non empty filters
     * @returns ProjectFilters | undefined
     */
    /* istanbul ignore next */
    getNonEmptyFilters() {
      const hasValue = (value?: number | null) => {
        return value !== null && value !== undefined;
      }
      const filters: { [key: string]: unknown } = {};
      for (const filterName in this.filters) {
        const key = filterName as keyof ProjectFilters;
        /* istanbul ignore if */
        if (
          this.filters[key] &&
          this.filters[key] !== null &&
          this.filters[key] !== undefined
        ) {
          /* istanbul ignore if-else */
          if (this.filters[key]?.hasOwnProperty("min") || this.filters[key]?.hasOwnProperty("max")) {
            const range = this.filters[key] as RangeFilter;
            if (hasValue(range.min) || hasValue(range.max)) {
              filters[key] = this.filters[key];
            }
          } else {
            filters[key] = this.filters[key];
          }
        }
      }
      return Object.values(filters).length
        ? (filters as ProjectFilters)
        : undefined;
    },

    /**
     * Return true if some filter is active
     */
    hasActiveFilter() {
      return Boolean(this.getNonEmptyFilters());
    },

    /**
     * Toggle Filter Value
     */
    toggleFilterValue(name, value) {
      const key = name as keyof StringFilterType;
      /* istanbul ignore if-else */
      if (this.filters?.[key]) {
        // Remove if value exists
        /* istanbul ignore if-else */
        // @ts-ignore Object possibly null or undefined - Checked if key exists above
        if (this.filters[key].includes(value)) {
          // @ts-ignore Object possibly null or undefined - Checked if key exists above
          this.filters[key] = this.filters[key].filter((item) => item !== value);
          // If this filter is an empty array remove it from Filter object
          if (!this.filters[key]?.length) {
            delete this.filters[key]
          }
        } else {
          // Add if value doesn't exists
          // @ts-ignore Object possibly null or undefined - Checked if key exists above
          this.filters[key].push(value);
        }
      } else {
        this.filters = {
          ...this.filters,
          [key]: [value]
        }
      }
      this.applyFilters();
    },

    /**
     * Change Filter Value
     */
    changeRangeFilterValue(name, value, subKey) {
      const key = name as keyof RangeFilterType;
      /* istanbul ignore if-else */
      if (!value?.length && this.filters && this.filters[key]) {
        delete this.filters[key]?.[subKey]
        if (
          // @ts-ignore - Object is possibly 'undefined' - Checked if key exists above
          'min' in this.filters[key] === false &&
          // @ts-ignore - Object is possibly 'undefined' - Checked if key exists above
          'max' in this.filters[key] === false
        ) {
          delete this.filters[key]
        }
      } else {
        if (this.filters?.[key]) {
          // @ts-ignore Object possibly null or undefined - Checked if key exists above
          this.filters[key][subKey] = value;
        } else {
          this.filters = {
            ...this.filters,
            [key]: {
              [subKey]: value
            }
          }
        }
      }
      this.applyFilters();
    },

    /**
     * Apply filters with 1sec delay on filter changes
     */
    /* istanbul ignore next */
    applyFilters() {
      clearTimeout(debounceTimer) // cancel prev call
      debounceTimer = setTimeout(() => {
        this
          .fetchProjectIds()
          .then(() => {
            if (!this.projectIds.length) {
              this.setNoProjectMessage(t("noprojects.filter_description"));
              return;
            }
            this.setPBIProjectFilter();
            this.setVisualLimitedView();
          })
      }, FILTER_APPLY_DELAY_MS)
    },

    /**
     * Get Range Filter Value
     */
    getRangeValue(name, subKey) {
      const key = name as keyof RangeFilterType;
      return this.filters?.[key]?.[subKey] || "";
    },

    /**
     * Get Filter Value Checked state
     */
    getFilterValueCheckedState(name, value) {
      const key = name as keyof StringFilterType;
      // @ts-ignore Object possibly null or undefined - Checked if key exists on line 268
      return Boolean(this.filters?.[key] && this.filters?.[key].includes(value));
    },

    /**
     * Set No Projects Message for Home page
     * @param value string
     */
    /* istanbul ignore next */
    setNoProjectMessage(value) {
      this.noProjectMessage = value;
    },

    /**
     * Fetch projects on page load based on preferences
     * Fetch projects on view change
     * Fetch projects on search text change
     * Set filter values
     */
    async fetchFilterValues() {
      const accessToken = await getTokenSilently();
      const result = await getProjects({
        accessToken,
        hasOverrides: this.hasOverrides,
        hasPredictions: this.hasPredictions,
        projectSearchText: this.searchText
      });
      if (result) {
        // Reset filterValues
        this.filterValues = {
          projectClientName: [],
          projectCompanyName: [],
          projectManagerName: [],
          projectDirectorName: [],
          projectOrganizationName: [],
        };
        const keys = Object.keys(this.filterValues) as (keyof StringFilterType)[];
        result.forEach((project) => {
          keys.forEach((key) => {
            const value = project[key] || "";
            if (!this.filterValues[key].includes(value)) {
              this.filterValues[key].push(value);
            }
          });
        });
        keys.forEach((key) => {
          this.filterValues[key] = this.filterValues[key].sort((a, b) => (a.localeCompare(b)));
        });
      }
    },

    /**
     * Fetch projectIds on page load based on preferences
     * Fetch projectIds on view change
     * Fetch projectIds on sort change
     * Fetch projectIds on filters change
     * Fetch projectIds on search text change
     * Set projectIds
     */
    /* istanbul ignore next */
    async fetchProjectIds() {
      const accessToken = await getTokenSilently();
      const result = await getProjectsIds({
        accessToken,
        sortBy: this.sortBy,
        limitSize: PROJECTS_LIMIT_SIZE,
        searchText: this.searchText,
        hasOverrides: this.hasOverrides,
        hasPredictions: this.hasPredictions,
        filters: this.getNonEmptyFilters()
      });
      runInAction(() => {
        this.projectIds = result?.projectIds || [];
        this.visualLimitedView = result?.isLimited || false;
        this.powerBIFilterLimitError = this.projectIds?.length > FILTER_VALUES_LIMIT;
      })
    },

    /**
     * Fetch filter items
     * Set filterItems
     */
    /* istanbul ignore next */
    async fetchFilterItems() {
      const accessToken = await getTokenSilently();
      const result = await getFilters(
        accessToken,
        azureIdentityId,
        hasSupportGroup()
      );
      runInAction(() => {
        this.filterItems = result?.map((item) => ({
          displayName: item.displayName,
          ...item.values,
        } as FilterItemType)) || [];
      });
    },

    /**
     * Fetch sort preference
     * Set sortBy
     */
    /* istanbul ignore next */
    async fetchSortPreference() {
      const result = await fetchPreference(ComponentsEnum.Sorts);
      this.setSortBy(result || "");
    },

    /**
     * Fetch view preference
     * Set view
     */
    /* istanbul ignore next */
    async fetchViewPreference() {
      const result = await fetchPreference(ComponentsEnum.Views);
      this.setSelectedView((result || ViewType.MyProjects) as ViewType);
    },

    /**
     * Save sort preference on change
     * @param value string
     */
    /* istanbul ignore next */
    async saveSortPreference(value) {
      await savePreference(
        ComponentsEnum.Sorts,
        {
          value,
          preferenceName: "Sort"
        }
      );
    },

    /**
     * Save view preference on change
     * @param value string
     */
    /* istanbul ignore next */
    async saveViewPreference(value) {
      await savePreference(
        ComponentsEnum.Views,
        {
          value,
          preferenceName: "View"
        }
      );
    },

    /**
     * Set PBI projectId filter
     */
    /* istanbul ignore next */
    async setPBIProjectFilter() {
      try {
        const projectIds = this.projectIds ? toJS(this.projectIds) : [];
        const basicFilters: IBasicFilter[] = [
          {
            $schema: "http://powerbi.com/product/schema#basic",
            target: {
              table: PROJECT_SUMMARY_TABLE,
              column: "ProjectId",
            },
            filterType: FilterType.Basic,
            operator: "In",
            values: projectIds || [],
          },
        ];
        const visual = await getVisualByGuid("projectSummary");

        if (!visual || !projectIds.length) {
          return;
        }
        visual?.updateFilters(FiltersOperations.Replace, basicFilters);
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * Set Visual Limited View property
     */
    /* istanbul ignore next */
    async setVisualLimitedView() {
      try {
        const visual = await getVisualByGuid("projectSummary");

        if (!visual) {
          return;
        }
        const limitedProjectsProperty = {
          objectName: "visualSettings",
          propertyName: "limitedProjects",
        };
        await visual.setProperty(limitedProjectsProperty, {
          value: toJS(this.visualLimitedView),
          schema: "http://powerbi.com/product/schema#property",
        });
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * Set Visual Sort properties
     */
    /* istanbul ignore next */
    async setVisualSort() {
      const visual = await getVisualByGuid("projectSummary");
      if (!visual) {
        return;
      }
      const sortProperty = {
        objectName: "visualSettings",
        propertyName: "userSortField",
      };
      const sortUIProperty = handleSortProperty(this.sortBy);
      try {
        await visual.setProperty(sortProperty, {
          value: this.sortBy || "DEFAULT_SORT",
          schema: "http://powerbi.com/product/schema#property",
        });
        await visual.setProperty(
          sortUIProperty.property,
          sortUIProperty.value
        );
      } catch (e) {
        console.log(e);
      }
    }
  }));
}
